import type { ElementType, ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Container, Section, and Eyebrow.
 *
 * - `Container` enforces the three canonical widths (prose / editorial / site)
 *   and the responsive padding documented in visual-identity.md §4.1.
 * - `Section` enforces the marketing rhythm py-16/20/28. Tool packages
 *   (`@sica/web`) override the className with denser padding per §11.3.
 * - `Eyebrow` renders the small uppercase kicker. The optional `n` prop
 *   prepends a numbered prefix used by Home and Producto sections.
 */

type Width = 'site' | 'editorial' | 'prose';

const widthClass: Record<Width, string> = {
  site: 'max-w-site',
  editorial: 'max-w-editorial',
  prose: 'max-w-prose',
};

export function Container({
  as: Component = 'div',
  width = 'site',
  className,
  children,
}: {
  as?: ElementType;
  width?: Width;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Component className={cn('mx-auto w-full px-4 sm:px-6 lg:px-8', widthClass[width], className)}>
      {children}
    </Component>
  );
}

export function Section({
  id,
  className,
  children,
  ariaLabelledby,
}: {
  id?: string;
  className?: string;
  children: ReactNode;
  ariaLabelledby?: string;
}) {
  return (
    <section
      id={id}
      aria-labelledby={ariaLabelledby}
      className={cn('py-16 sm:py-20 lg:py-28', className)}
    >
      {children}
    </section>
  );
}

export function Eyebrow({
  n,
  children,
  className,
}: {
  n?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={cn('eyebrow', className)}>
      {n && (
        <>
          <span className="text-accent">{n}</span>
          <span className="mx-1.5 text-ink-subtle" aria-hidden="true">
            ·
          </span>
        </>
      )}
      <span>{children}</span>
    </p>
  );
}
